import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

export interface LigneRupture {
  id: string;
  apprenantId: string;
  apprenant: string;
  formation: string | null;
  entreprise: string;
  siteCode: string;
  /** Vide tant que la date n'est pas arrêtée. */
  dateRupture: string | null;
  motif: string;
  formationMaintenue: boolean;
  pendantPeriodeEssai: boolean;
  documentId: string | null;
  commentaire?: string | null;
  dateDebutContrat?: string | null;
  dateFinContrat?: string | null;
}

export interface ContratEnCours {
  apprenantId: string;
  apprenant: string;
  formation: string | null;
  entreprise: string;
  siteCode: string;
  dateDebut: string;
  finPeriodeEssai: string | null;
}

export interface CompteursRuptures {
  ruptures: number;
  contrats: number;
  tauxPourcent: number;
  formationMaintenue: number;
  pendantPeriodeEssai: number;
  delaiMoyenJours: number | null;
}

export interface ReponseRuptures {
  ruptures: LigneRupture[];
  compteurs: CompteursRuptures;
  motifs: { motif: string; nombre: number }[];
  contratsEnCours: ContratEnCours[];
  motifsDisponibles: string[];
}

export function useRuptures(site: string, active = true) {
  const parametres = new URLSearchParams({ site });
  return useQuery({
    queryKey: ['ruptures', site],
    queryFn: () => appelApi<ReponseRuptures>(`/ruptures?${parametres.toString()}`),
    enabled: active,
  });
}

/**
 * URL de téléchargement du tableau, au format du classeur Excel tenu par TOP
 * FORMATION : le navigateur porte le cookie de session.
 */
export function urlExportRuptures(site: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/ruptures/export.xlsx?${new URLSearchParams({ site }).toString()}`;
}

export interface ResultatRupture {
  ruptureId: string;
  documentId: string;
  formationMaintenue: boolean;
  informes: number;
  /** Ce qui reste à faire à la main sur le document produit. */
  avertissements: string[];
}

/**
 * Déclaration de rupture (CDC §4.6). Le maintien de la formation est décidé ici :
 * l'API bascule ensuite le dossier en recherche d'entreprise et informe les pôles.
 */
export function useDeclarerRupture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      apprenantId: string;
      dateRupture?: string;
      motif: string;
      formationMaintenue: boolean;
      commentaire?: string;
    }) => {
      const { apprenantId, ...corps } = variables;
      return appelApi<ResultatRupture>(`/apprenants/${apprenantId}/rupture`, {
        methode: 'POST',
        corps,
      });
    },
    onSuccess: (_resultat, variables) => {
      void client.invalidateQueries({ queryKey: ['ruptures'] });
      void client.invalidateQueries({ queryKey: ['apprenants'] });
      void client.invalidateQueries({ queryKey: ['dossier', variables.apprenantId] });
    },
  });
}

export interface ResultatDateRupture {
  ruptureId: string;
  dateRupture: string;
  documentId: string | null;
  /** Faux quand le formulaire, déjà signé ou en signature, est conservé tel quel. */
  formulaireRegenere: boolean;
  informes: number;
  avertissements: string[];
}

/**
 * Date d'une rupture déclarée sans elle. L'API régénère le formulaire, sauf
 * s'il est déjà signé : la date s'y porte alors à la main.
 */
export function useCompleterDateRupture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { ruptureId: string; apprenantId: string; dateRupture: string }) =>
      appelApi<ResultatDateRupture>(`/ruptures/${variables.ruptureId}/date`, {
        methode: 'PATCH',
        corps: { dateRupture: variables.dateRupture },
      }),
    onSuccess: (_resultat, variables) => {
      void client.invalidateQueries({ queryKey: ['ruptures'] });
      void client.invalidateQueries({ queryKey: ['dossier', variables.apprenantId] });
    },
  });
}
